import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FiClock, FiPhone, FiFileText, FiMessageSquare,
  FiCalendar, FiEdit3, FiGlobe, FiSettings,
} from 'react-icons/fi';
import sonantLogo from '../../assets/images/sonant.png';
const getToken = () => localStorage.getItem('toolboxToken') || '';

const FEATURES = [
  { Icon: FiClock,        title: '24/7 Availability',        description: 'Enable 24/7 personalized and consistent service with zero waiting times for your clients.' },
  { Icon: FiPhone,        title: 'Transfer Calls',            description: 'Direct to human agents as needed. Instantly connects customers to your team or third parties.' },
  { Icon: FiFileText,     title: 'Quote Intaking',            description: 'Make quote intaking an automatic process, from a call to quote details in your AMS or rater.' },
  { Icon: FiMessageSquare,title: 'Servicing Inquiries',       description: 'Collect information relative with requests from customers and third parties.' },
  { Icon: FiCalendar,     title: 'Appointment Scheduling',    description: 'Automatically schedules appointments, fully integrated with your calendar.' },
  { Icon: FiEdit3,        title: 'Post Call Notes',           description: 'Summarizes calls and extracts relevant data and automatically updates your systems.' },
  { Icon: FiGlobe,        title: 'Multilingual',              description: 'Speaks and understands multiple languages, even in the same call.' },
  { Icon: FiSettings,     title: 'Tailored to your Agency',   description: 'Consistent client experience across calls, tailored to your agency guidelines.' },
];

const SonantLandingView: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios({
      method: 'get',
      url: `${(process.env.REACT_APP_DataFabric_Url || '')}/api/CallNotification/sonantdetails`,
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          const { sonantCustomerId, apiKey: key } = res.data.data;
          setCustomerId(sonantCustomerId || '');
          setApiKey(key || '');
          if (
            sonantCustomerId !== '' &&
            sonantCustomerId !== null &&
            key !== '' &&
            key !== null
          ) {
            setIsSubscribed(true);
          } else {
            setIsSubscribed(false);
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.status === 401) {
          navigate('/');
        } else {
          setError('Unable to load Sonant configuration.');
        }
      });
  }, [navigate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header card ── */}
      <div style={{
        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
        borderRadius: 12, padding: '18px 20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <img src={sonantLogo} alt="Sonant" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'contain' }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700 }}>Sonant</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>The AI Receptionist for Insurance Agencies &amp; Brokers</div>
          </div>
          {!loading && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600,
              background: isSubscribed ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.05)',
              border: isSubscribed ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(255,255,255,0.1)',
              color: isSubscribed ? '#10b981' : 'rgba(255,255,255,0.35)',
            }}>
              {isSubscribed ? '✓ Active' : 'Not Subscribed'}
            </span>
          )}
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.75, margin: '0 0 14px' }}>
          Sonant AI offers an AI-powered receptionist solution tailored for insurance agencies. Their technology
          automates routine phone calls, including servicing requests, quote intakes, lead qualification, and
          appointment scheduling, allowing agents to focus on higher-value tasks.
        </p>

        <button
          onClick={() => window.open('https://tally.so/r/npvP1B', '_blank')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'linear-gradient(135deg, #A200FF, #7c3aed)',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 20px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Request a Demo
        </button>
      </div>

      {/* ── Services grid ── */}
      <div style={{
        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
        borderRadius: 12, padding: '18px 20px',
      }}>
        <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Services</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16 }}>
          {FEATURES.map(({ Icon, title, description }) => (
            <div key={title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{
                flexShrink: 0, width: 32, height: 32, borderRadius: 8,
                background: 'rgba(162,0,255,0.1)', border: '1px solid rgba(162,0,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A200FF',
              }}>
                <Icon size={16} />
              </div>
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{title}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.6 }}>{description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Configuration ── */}
      <div style={{
        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
        borderRadius: 12, padding: '18px 20px',
      }}>
        <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Configuration</div>

        {error && (
          <div style={{
            color: '#ef4444', fontSize: 12, marginBottom: 12,
            padding: '8px 12px', borderRadius: 8,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2].map((i) => (
              <div key={i} style={{
                height: 36, borderRadius: 8,
                background: 'rgba(255,255,255,0.04)',
                animation: 'tbx-shimmer 1.5s ease-in-out infinite',
              }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              { label: 'Customer ID', value: customerId },
              { label: 'API Key',     value: apiKey },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{
                  color: 'var(--text-secondary)', fontSize: 10, fontWeight: 600,
                  marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  {label}
                </div>
                <input
                  readOnly
                  value={value}
                  placeholder={`No ${label.toLowerCase()} configured`}
                  style={{
                    width: '100%', padding: '8px 12px', fontSize: 12, boxSizing: 'border-box',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-glass)',
                    borderRadius: 8, color: value ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SonantLandingView;

